import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Palette from "./components/Palette.jsx";
import Canvas from "./components/Canvas.jsx";
import LayersPanel from "./components/LayersPanel.jsx";
import CanvasSetup from "./components/CanvasSetup.jsx";
import BottomToolbar from "./components/BottomToolbar.jsx";
import TopBar from "./components/TopBar.jsx";
import ContextMenu from "./components/ContextMenu.jsx";
import Icon from "./components/Icon.jsx";
import { useCanvas, usedSegmentIds } from "./state/useCanvas.js";
import { useCanvasFile } from "./state/useCanvasFile.js";
import { usePalette } from "./state/usePalette.js";
import { useFavorites } from "./state/useFavorites.js";
import { postRecompose, segmentUrl } from "./api.js";
import { loadImages } from "./lib/imageCache.js";

export default function App() {
  const file = useCanvasFile();
  const canvas = useCanvas(file.currentId);

  const used = useMemo(() => usedSegmentIds(canvas.state), [canvas.state]);
  const [paletteSize, setPaletteSize] = useState(8);
  const palette = usePalette({ size: paletteSize, excludeIds: used });

  const [recomposeStatus, setRecomposeStatus] = useState(null);
  const [contextMenu, setContextMenu] = useState(null); // { x, y, items }
  // Palette has two modes: "random" (default — shows random segments)
  // and "favorites" (shows everything the user has starred). Toggled by
  // the dice and star buttons in the palette.
  const [paletteMode, setPaletteMode] = useState("random");
  const favorites = useFavorites();

  const canvasRef = useRef(null);

  // While in favorites mode, keep the displayed list fresh: refresh
  // whenever it's been invalidated (a toggle elsewhere) or when we
  // first switch in.
  useEffect(() => {
    if (paletteMode !== "favorites") return;
    if (!favorites.listLoaded) favorites.refreshList();
  }, [paletteMode, favorites.listLoaded, favorites.refreshList]);

  // Tiles fed into the palette depend on the mode. In favorites mode the
  // displayed list is filtered by the live ids Set so an unfavorite
  // optimistically removes the tile without waiting for a refresh.
  const paletteTiles =
    paletteMode === "favorites"
      ? favorites.list.filter((t) => favorites.ids.has(t.id))
      : palette.tiles;

  // ---- Keyboard ----------------------------------------------------------
  useEffect(() => {
    const onKey = (e) => {
      const tag = document.activeElement?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || document.activeElement?.isContentEditable) return;
      const meta = e.metaKey || e.ctrlKey;
      const key = e.key.toLowerCase();

      // Backspace / Delete (without modifiers) deletes the selection.
      if ((e.key === "Backspace" || e.key === "Delete") && !meta && !e.altKey) {
        if (canvas.state.selectedIds.length > 0) {
          e.preventDefault();
          canvas.removeLayers(canvas.state.selectedIds);
        }
        return;
      }

      if (!meta) return;
      if (key === "z" && !e.shiftKey) { e.preventDefault(); canvas.undo(); }
      else if ((key === "z" && e.shiftKey) || key === "y") { e.preventDefault(); canvas.redo(); }
      else if (key === "d") {
        e.preventDefault();
        if (canvas.state.selectedIds.length > 0) canvas.duplicateLayers(canvas.state.selectedIds);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [canvas]);

  const selectedLayers = useMemo(
    () => canvas.state.layers.filter((l) => canvas.state.selectedIds.includes(l.id)),
    [canvas.state.layers, canvas.state.selectedIds],
  );
  const single = selectedLayers.length === 1 ? selectedLayers[0] : null;

  // ---- Palette / drag-in -------------------------------------------------
  const handleAddLayerFromTile = useCallback(
    async (tile, dropPos) => {
      try { await loadImages([segmentUrl(tile.file)]); } catch {}
      canvas.addLayer({
        x: dropPos.x,
        y: dropPos.y,
        members: [
          { segmentId: tile.segmentId, file: tile.file, dx: 0, dy: 0, w: tile.w, h: tile.h },
        ],
      });
      // In random mode, replace the dragged-out tile's slot with a
      // sibling. In favorites mode, the palette IS the favorites list —
      // don't mutate it on drag-out (a favorite stays favorited and
      // re-usable until the user explicitly unfavorites).
      if (paletteMode === "random") {
        palette.removeAndRefill(tile.tileIdx, tile.sourceId, tile.segmentId);
      }
    },
    [canvas, palette, paletteMode],
  );

  // ---- Recompose / siblings ----------------------------------------------
  const handleRecompose = useCallback(async () => {
    if (!single) return;
    setRecomposeStatus("running");
    const anchorId = single.members[0].segmentId;
    const memberIds = single.members.map((m) => m.segmentId);
    try {
      const added = await postRecompose(anchorId, memberIds);
      if (!added || added.length === 0) {
        setRecomposeStatus("no-neighbors");
        setTimeout(() => setRecomposeStatus(null), 1500);
        return;
      }
      await loadImages(added.map((n) => segmentUrl(n.file))).catch(() => {});
      canvas.recomposeLayer(single.id, added.map((n) => ({
        segmentId: n.id, file: n.file, dx: n.dx, dy: n.dy, w: n.w, h: n.h,
      })));
      setRecomposeStatus(null);
    } catch (err) {
      console.warn("recompose failed:", err);
      setRecomposeStatus(null);
    }
  }, [single, canvas]);

  const handleAddSiblings = useCallback(() => {
    if (!single) return;
    palette.injectSiblings(single.members[0].segmentId);
  }, [single, palette]);

  // ---- Export ------------------------------------------------------------
  const downloadDataUrl = (dataUrl, filename) => {
    if (!dataUrl) return;
    const a = document.createElement("a");
    a.href = dataUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
  };
  const handleExportCanvas = useCallback(() => {
    const url = canvasRef.current?.exportStage({ pixelRatio: 2 });
    const ts = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 19);
    const slug = (file.current?.name || "collage").replace(/[^\w-]+/g, "-").toLowerCase();
    downloadDataUrl(url, `${slug}-${ts}.png`);
  }, [file.current]);
  const handleExportSelection = useCallback(() => {
    if (canvas.state.selectedIds.length === 0) return handleExportCanvas();
    const url = canvasRef.current?.exportNodes(canvas.state.selectedIds, { pixelRatio: 2 });
    const ts = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 19);
    const slug = (file.current?.name || "collage").replace(/[^\w-]+/g, "-").toLowerCase();
    downloadDataUrl(url, `${slug}-selection-${ts}.png`);
  }, [canvas.state.selectedIds, handleExportCanvas, file.current]);

  // ---- Send to new canvas ------------------------------------------------
  const sendLayersToNewCanvas = useCallback(
    async (layerIds, { mode, name } = { mode: "move" }) => {
      const ids = new Set(layerIds);
      const movedLayers = canvas.state.layers.filter((l) => ids.has(l.id));
      const sourceGroupIds = new Set(movedLayers.map((l) => l.groupId).filter(Boolean));
      const sourceGroups = canvas.state.groups.filter((g) => sourceGroupIds.has(g.id));
      // Re-center the moved layers near the new canvas origin so the
      // file doesn't open at some weird offscreen position.
      const xs = movedLayers.map((l) => l.transform.x);
      const ys = movedLayers.map((l) => l.transform.y);
      const cx = (Math.min(...xs) + Math.max(...xs)) / 2 || 600;
      const cy = (Math.min(...ys) + Math.max(...ys)) / 2 || 360;
      const offsetX = 600 - cx;
      const offsetY = 360 - cy;
      // Layers in the destination canvas get re-centered AND ungrouped:
      // the new canvas starts with a clean slate, so there's no point
      // carrying the group identity (the user can re-group there if
      // they want).
      const fresh = movedLayers.map((l) => ({
        ...l,
        groupId: null,
        transform: { ...l.transform, x: l.transform.x + offsetX, y: l.transform.y + offsetY },
      }));
      const payload = {
        schema: 2,
        layers: fresh,
        groups: [],
        selectedIds: [],
        canvasSize: { w: canvas.state.canvasSize?.w || 1200, h: canvas.state.canvasSize?.h || 720 },
      };
      const newName = name
        || (sourceGroups[0]?.name)
        || `From ${movedLayers[0]?.members[0]?.file?.replace(/_seg\d+\.png$/i, "") || "selection"}`;
      await file.create({ name: newName.slice(0, 64), payload });
      if (mode === "move") canvas.removeLayers([...ids]);
    },
    [canvas, file],
  );

  // ---- Context menu builders ---------------------------------------------
  const closeContextMenu = useCallback(() => setContextMenu(null), []);

  // Collect every segmentId across the given layer ids (a layer may have
  // multiple members from prior recompose passes).
  const segmentsInLayers = useCallback(
    (layerIds) => {
      const out = [];
      const seen = new Set();
      for (const l of canvas.state.layers) {
        if (!layerIds.includes(l.id)) continue;
        for (const m of l.members) {
          if (Number.isFinite(m.segmentId) && !seen.has(m.segmentId)) {
            seen.add(m.segmentId);
            out.push(m.segmentId);
          }
        }
      }
      return out;
    },
    [canvas.state.layers],
  );

  // Favorite all segments in the given layers; if every segment is
  // already favorited, unfavorite them all instead.
  const toggleFavoritesForLayers = useCallback(
    (layerIds) => {
      const segIds = segmentsInLayers(layerIds);
      if (segIds.length === 0) return;
      const allFav = segIds.every((id) => favorites.ids.has(id));
      if (allFav) {
        for (const id of segIds) favorites.toggle(id);
      } else {
        for (const id of segIds) {
          if (!favorites.ids.has(id)) favorites.toggle(id);
        }
      }
    },
    [favorites, segmentsInLayers],
  );

  const buildLayerMenuItems = useCallback(
    (layerId) => {
      const ids = canvas.state.selectedIds.includes(layerId)
        ? canvas.state.selectedIds
        : [layerId];
      const isSingle = ids.length === 1;
      const layersInScope = canvas.state.layers.filter((l) => ids.includes(l.id));
      const allHidden = layersInScope.length > 0 && layersInScope.every((l) => l.hidden);
      const allLocked = layersInScope.length > 0 && layersInScope.every((l) => l.locked);
      const segIds = segmentsInLayers(ids);
      const allFav = segIds.length > 0 && segIds.every((id) => favorites.ids.has(id));
      const favLabel = (() => {
        if (segIds.length === 0) return "Favorite";
        if (allFav) return segIds.length > 1 ? `Unfavorite ${segIds.length} segments` : "Unfavorite segment";
        return segIds.length > 1 ? `Favorite ${segIds.length} segments` : "Favorite segment";
      })();
      return [
        {
          label: favLabel,
          icon: "star",
          tone: allFav ? "default" : "cyan",
          disabled: segIds.length === 0,
          onClick: () => toggleFavoritesForLayers(ids),
        },
        { divider: true },
        {
          label: allHidden ? (ids.length > 1 ? "Show layers" : "Show layer") : (ids.length > 1 ? "Hide layers" : "Hide layer"),
          onClick: () => canvas.setLayerHidden(ids, !allHidden),
        },
        {
          label: allLocked ? (ids.length > 1 ? "Unlock layers" : "Unlock layer") : (ids.length > 1 ? "Lock layers" : "Lock layer"),
          onClick: () => canvas.setLayerLocked(ids, !allLocked),
        },
        { divider: true },
        {
          label: "Bring to front",
          onClick: () => canvas.bringToFront(ids),
        },
        {
          label: "Send to back",
          onClick: () => canvas.sendToBack(ids),
        },
        {
          label: "Duplicate",
          shortcut: "⌘D",
          onClick: () => canvas.duplicateLayers(ids),
        },
        { divider: true },
        {
          label: "Flip horizontal",
          icon: "flip-horizontal",
          onClick: () => canvas.toggleMirror(ids, "h"),
        },
        {
          label: "Flip vertical",
          icon: "flip-vertical",
          onClick: () => canvas.toggleMirror(ids, "v"),
        },
        {
          label: "Recompose",
          icon: "recompose",
          disabled: !isSingle,
          onClick: () => handleRecompose(),
        },
        {
          label: "Siblings → palette",
          icon: "siblings-to-palette",
          disabled: !isSingle,
          onClick: () => handleAddSiblings(),
        },
        { divider: true },
        {
          label: ids.length > 1 ? `Move ${ids.length} to new canvas` : "Move to new canvas",
          icon: "export-canvas",
          tone: "cyan",
          onClick: () => sendLayersToNewCanvas(ids, { mode: "move" }),
        },
        {
          label: "Copy to new canvas",
          icon: "export-selection",
          onClick: () => sendLayersToNewCanvas(ids, { mode: "copy" }),
        },
        { divider: true },
        {
          label: ids.length > 1 ? `Delete ${ids.length} layers` : "Delete layer",
          icon: "delete",
          tone: "coral",
          onClick: () => canvas.removeLayers(ids),
        },
      ];
    },
    [canvas, favorites, segmentsInLayers, toggleFavoritesForLayers, handleRecompose, handleAddSiblings, sendLayersToNewCanvas],
  );

  const buildGroupMenuItems = useCallback(
    (groupId) => {
      const memberLayers = canvas.state.layers.filter((l) => l.groupId === groupId);
      const memberIds = memberLayers.map((l) => l.id);
      const group = canvas.state.groups.find((g) => g.id === groupId);
      const allHidden = memberLayers.length > 0 && memberLayers.every((l) => l.hidden);
      const allLocked = memberLayers.length > 0 && memberLayers.every((l) => l.locked);
      const segIds = segmentsInLayers(memberIds);
      const allFav = segIds.length > 0 && segIds.every((id) => favorites.ids.has(id));
      const favLabel = segIds.length === 0
        ? "Favorite"
        : allFav
        ? `Unfavorite ${segIds.length} segment${segIds.length === 1 ? "" : "s"}`
        : `Favorite ${segIds.length} segment${segIds.length === 1 ? "" : "s"}`;
      return [
        {
          label: "Rename group…",
          onClick: () => {
            const next = prompt("Rename group", group?.name || "Group");
            if (next != null && next.trim()) canvas.renameGroup(groupId, next.trim());
          },
        },
        { divider: true },
        {
          label: favLabel,
          icon: "star",
          tone: allFav ? "default" : "cyan",
          disabled: segIds.length === 0,
          onClick: () => toggleFavoritesForLayers(memberIds),
        },
        {
          label: allHidden ? "Show group" : "Hide group",
          onClick: () => canvas.setLayerHidden(memberIds, !allHidden),
        },
        {
          label: allLocked ? "Unlock group" : "Lock group",
          onClick: () => canvas.setLayerLocked(memberIds, !allLocked),
        },
        { divider: true },
        {
          label: "Bring group to front",
          onClick: () => canvas.bringToFront(memberIds),
        },
        {
          label: "Send group to back",
          onClick: () => canvas.sendToBack(memberIds),
        },
        {
          label: "Duplicate group",
          shortcut: "⌘D",
          onClick: () => canvas.duplicateLayers(memberIds),
        },
        { divider: true },
        {
          label: "Flip horizontal",
          icon: "flip-horizontal",
          onClick: () => canvas.toggleMirror(memberIds, "h"),
        },
        {
          label: "Flip vertical",
          icon: "flip-vertical",
          onClick: () => canvas.toggleMirror(memberIds, "v"),
        },
        { divider: true },
        {
          label: "Send group to new canvas",
          icon: "export-canvas",
          tone: "cyan",
          onClick: () => sendLayersToNewCanvas(memberIds, { mode: "move", name: group?.name }),
        },
        {
          label: "Copy group to new canvas",
          icon: "export-selection",
          onClick: () => sendLayersToNewCanvas(memberIds, { mode: "copy", name: group?.name }),
        },
        { divider: true },
        {
          label: "Ungroup",
          icon: "ungroup",
          onClick: () => canvas.ungroup(groupId),
        },
        {
          label: "Delete group",
          icon: "delete",
          tone: "coral",
          onClick: () => canvas.removeLayers(memberIds),
        },
      ];
    },
    [canvas, favorites, segmentsInLayers, toggleFavoritesForLayers, sendLayersToNewCanvas],
  );

  // ---- Render ------------------------------------------------------------
  return (
    <div className="grid h-full grid-rows-[48px_140px_minmax(0,1fr)] grid-cols-[260px_minmax(0,1fr)] overflow-hidden">
      <div className="col-span-2 row-start-1 min-h-0">
        <TopBar
          list={file.list}
          current={file.current}
          loading={file.loading}
          onSwitch={file.switchTo}
          onCreate={file.create}
          onRename={file.rename}
          onDelete={file.remove}
        />
      </div>

      <div className="col-span-2 row-start-2 min-h-0">
        <Palette
          tiles={paletteTiles}
          loading={paletteMode === "random" ? palette.loading : false}
          mode={paletteMode}
          onReshuffle={() => {
            setPaletteMode("random");
            palette.reshuffle();
          }}
          onShowFavorites={() => {
            setPaletteMode("favorites");
            favorites.refreshList();
          }}
          onTileDragStart={() => canvas.clearSelection()}
          onSizeChange={setPaletteSize}
          isFavorite={(id) => favorites.ids.has(id)}
          onToggleFavorite={favorites.toggle}
          favoritesCount={favorites.ids.size}
        />
      </div>

      <div className="col-start-1 row-start-3 flex min-h-0 min-w-0 flex-col">
        <CanvasSetup
          width={canvas.state.canvasSize?.w || 1200}
          height={canvas.state.canvasSize?.h || 720}
          onChange={canvas.setCanvasSize}
        />
        <LayersPanel
          layers={canvas.state.layers}
          groups={canvas.state.groups}
          selectedIds={canvas.state.selectedIds}
          onSelectLayers={canvas.selectLayers}
          onToggleSelection={canvas.toggleLayerSelection}
          onRemoveLayers={canvas.removeLayers}
          onReorder={canvas.reorderLayers}
          onCreateGroup={(ids) => canvas.createGroup(ids)}
          onUngroup={canvas.ungroup}
          onToggleCollapse={canvas.toggleGroupCollapse}
          onRenameGroup={canvas.renameGroup}
          onSetLayerLocked={canvas.setLayerLocked}
          onSetLayerHidden={canvas.setLayerHidden}
          onLayerContextMenu={({ layerId, x, y }) =>
            setContextMenu({ x, y, items: buildLayerMenuItems(layerId) })
          }
          onGroupContextMenu={({ groupId, x, y }) =>
            setContextMenu({ x, y, items: buildGroupMenuItems(groupId) })
          }
        />
      </div>

      <div className="col-start-2 row-start-3 flex flex-col min-h-0 min-w-0">
        <div className="relative flex-1 overflow-hidden">
          <Canvas
            ref={canvasRef}
            layers={canvas.state.layers}
            selectedIds={canvas.state.selectedIds}
            canvasSize={canvas.state.canvasSize || { w: 1200, h: 720 }}
            onSelectLayers={canvas.selectLayers}
            onToggleSelection={canvas.toggleLayerSelection}
            onTransformLayer={canvas.transformLayer}
            onTransformLayers={canvas.transformLayers}
            onDuplicateLayersInPlace={canvas.duplicateLayersInPlace}
            onAddLayerFromTile={handleAddLayerFromTile}
            onLayerContextMenu={({ layerId, x, y }) =>
              setContextMenu({ x, y, items: buildLayerMenuItems(layerId) })
            }
          />
          <ExportControls
            canExportSelection={canvas.state.selectedIds.length > 0}
            onExportCanvas={handleExportCanvas}
            onExportSelection={handleExportSelection}
          />
        </div>
        <BottomToolbar
          selectedLayers={selectedLayers}
          canUndo={canvas.canUndo}
          canRedo={canvas.canRedo}
          onUndo={canvas.undo}
          onRedo={canvas.redo}
          onMirrorH={() => canvas.toggleMirror(canvas.state.selectedIds, "h")}
          onMirrorV={() => canvas.toggleMirror(canvas.state.selectedIds, "v")}
          onRecompose={handleRecompose}
          onAddSiblings={handleAddSiblings}
          onRemove={() => canvas.removeLayers(canvas.state.selectedIds)}
          recomposeStatus={recomposeStatus}
        />
      </div>

      {contextMenu && (
        <ContextMenu
          x={contextMenu.x}
          y={contextMenu.y}
          items={contextMenu.items}
          onClose={closeContextMenu}
        />
      )}
    </div>
  );
}

function ExportControls({ canExportSelection, onExportCanvas, onExportSelection }) {
  return (
    <div className="absolute right-4 top-4 flex items-center gap-2">
      {canExportSelection && (
        <button
          onClick={onExportSelection}
          title="Export the selected layers' bounding box as a PNG"
          className="flex items-center gap-1.5 rounded-2xl border-2 border-ink bg-white px-3 py-2 text-sm font-semibold text-ink shadow-card transition hover:bg-paper-deep hover:-translate-y-0.5"
        >
          <Icon name="export-selection" size={20} alt="" />
          Export selection
        </button>
      )}
      <button
        onClick={onExportCanvas}
        title="Export the entire canvas as a PNG"
        className="flex items-center gap-1.5 rounded-2xl border-2 border-ink bg-cyan-soft px-3 py-2 text-sm font-semibold text-ink shadow-plump transition hover:bg-cyan-bright hover:-translate-y-0.5"
      >
        <Icon name="export-canvas" size={20} alt="" />
        Export canvas
      </button>
    </div>
  );
}
